<template>
    <div class="mb-8" :id="`addressItem${this.subscription.id}`">
        <box-accordion
            v-if="!showEditForm && !showPauseForm"
            :force-expand-state="shouldExpand"
            @toggleExpand="toggleExpand">
            <template slot="summary">
                <p class="m-0 p-4 text-left contentsquare_sensitive_data">
                    <span v-text="address.address1"></span><br/>
                    <span v-text="address.city"></span><br/>
                    <span v-text="address.postcode"></span>
                </p>
            </template>
            <div>
                <!-- Quantity -->
                <address-manage-quantity
                    class="option__top-border"
                    v-if="allowEdit"
                    :subscription="subscription"/>

                <select-box-type v-if="bumperBoxEnabled" :subscription="subscription"/>

                <delivery-price headingKey="MANAGE_DELIVERY_COST_EACH" :price="subscriptionsPrice"/>

                <!-- Edit address -->
                <div class="deliveryAddressCard__action edit-option option__top-border" :class="{'border-bottom': !pauseAllowed}" v-if="allowEdit" @click="toggleEditForm">
                    <p class="no-margin" v-text="$t('ADDRESS_ITEM_EDIT')"></p>
                    <img class="edit-option__icon" src="@/assets/images/decoration/arrow-right-black.svg">
                </div>

                <pause-button
                    v-if="!allowDelete && pauseAllowed"
                    class="option__top-border "
                    :subscription="subscription"
                    @toggleCantPauseModal="toggleCantPauseModal"/>

                <!-- Cancel -->
                <div v-if="!allowDelete" class="deliveryAddressCard__action cancel-option" @click="goToUpdatePlan()">
                    <p class="no-margin" v-text="$t('ADDRESS_ITEM_STOP')"></p>
                    <img class="cancel-option__icon" src="@/assets/images/decoration/nav-close.svg">
                </div>

                <!-- Restart -->
                <restart-deliveries-action
                    v-if="allowRestart"
                    @restarted="$emit('closeList')"
                    :subscription="subscription"
                    :address="address"/>

                <div class="deliveryAddressCard__action" v-if="allowDelete" @click="toggleDeleteAddressModal">
                    <p class="no-margin" v-text="$t('DELETE_SAVED_ADDRESSES')"></p>
                    <img class="deliveryAddressCard__actionIcon" src="@/assets/images/decoration/nav-close.svg">
                </div>
            </div>
        </box-accordion>

        <edit-delivery-address-form
            v-if="showEditForm"
            :subscription-id="subscriptionId"
            :address="address"
            :delivery-comment="subscription.delivery_comment"
            :property-type="address.property_type"
            @closed="toggleEditForm"/>

        <pause-deliveries
            v-if="showPauseForm"
            :subscriptionId="subscription.id"
            :addressId="addressId"
            :isSubscriptionPaused="false"
            @deliveries-paused="handleDeliveriesPaused"
            @closed="togglePauseForm"/>

        <pause-deliveries-confirmation-modal @pause-deliveries-modal-closed="handleConfirmationModalClosed" :subscription-id="subscriptionId" v-if="showPauseConfirmationModal"/>

        <cancel-subscription-modal
            v-if="showCancelModal"
            :subscription-id="subscriptionId"
            @closed="toggleCancelModal"/>

        <delete-addresses-modal
            v-if="showDeleteAddressModal"
            :address-id="addressId"
            @closed="toggleDeleteAddressModal"/>

        <cannot-pause-modal v-if="showCantPauseModal" :isPause="false"/>
    </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { SUBSCRIPTION_STATUS_CANCELLED } from '@/constants/Subscription';
import BoxAccordion from '@/components/structure/BoxAccordion';
import CancelSubscriptionModal from '@/components/modals/CancelSubscriptionModal';
import PauseDeliveriesConfirmationModal from '@/components/modals/PauseDeliveriesConfirmationModal';
import EditDeliveryAddressForm from '@/components/forms/addresses/EditDeliveryAddressForm';
import DeliveryPrice from '@/components/partials/account/my-delivery-addresses/DeliveryPrice';
import AddressManageQuantity from '@/components/partials/account/my-delivery-addresses/AddressManageQuantity';
import RestartDeliveriesAction from '@/components/partials/account/my-delivery-addresses/RestartDeliveriesAction';
import PauseButton from './PauseButton.vue';
import PauseDeliveries from '../my-delivery-schedule/manage-delivery/PauseDeliveries.vue';
import DeleteAddressesModal from '@/components/modals/DeleteAddressesModal';
import SelectBoxType from '@/components/partials/account/my-delivery-addresses/SelectBoxType';
import CannotPauseModal from '@/components/modals/CannotPauseModal';

export default {
    name: 'AddressItem',
    data() {
        return {
            showCancelModal: false,
            showEditForm: false,
            showPauseForm: false,
            showPauseConfirmationModal: false,
            showDeleteAddressModal: false,
            showCantPauseModal: false,
        };
    },
    props: {
        addressId: {
            type: Number,
            required: true,
        },
        allowEdit: {
            default: false,
            type: Boolean,
        },
        allowCancel: {
            type: Boolean,
            default: false,
        },
        allowRestart: {
            type: Boolean,
            default: false,
        },
        allowPause: {
            type: Boolean,
            default: true,
        },
        shouldExpand: {
            required: true,
        },
        subscriptionId: {
            type: Number,
            required: true,
        },
    },
    mounted() {
        this.$store.dispatch('address/fetchAllAddresses');
    },
    methods: {
        toggleCancelModal() {
            this.showCancelModal = !this.showCancelModal;
        },

        toggleEditForm() {
            this.showPauseForm = false;
            this.showEditForm = !this.showEditForm;
        },

        togglePauseForm() {
            this.showEditForm = false;
            this.showPauseForm = !this.showPauseForm;
        },

        handleDeliveriesPaused() {
            this.showPauseConfirmationModal = true;
        },

        handleConfirmationModalClosed() {
            this.showPauseConfirmationModal = false;
        },

        toggleExpand() {
            this.$emit('expandSubscription', this.isExpanded === false ? this.subscriptionId : 0);
        },

        toggleDeleteAddressModal() {
            this.showDeleteAddressModal = !this.showDeleteAddressModal;
        },

        toggleCantPauseModal() {
            this.showCantPauseModal = !this.showCantPauseModal;
        },

        goToUpdatePlan() {
            if (!this.getAllowCancel) {
                return this.toggleCantPauseModal();
            }
            this.$store.commit('account/setIsPauseFlow', false);
            this.$store.commit('subscription/setActiveSubscriptionId', this.subscriptionId);
            return this.$router.push({ name: 'account.update-your-plan' });
        },
    },
    computed: {
        ...mapGetters({
            pauseAllowed: 'account/getAllowPaused',
            getAllowCancel: 'account/getAllowCancel',
        }),
        address() {
            return this.$store.getters['address/getAddressById'](this.addressId);
        },
        allowDelete() {
            return this.subscription.status === SUBSCRIPTION_STATUS_CANCELLED;
        },
        subscription() {
            return this.$store.getters['subscription/getSubscriptionById'](this.subscriptionId);
        },
        bumperBoxEnabled() {
            return this.$store.getters['account/getBumperBoxEnabled'];
        },
        isExpanded() {
            return this.shouldExpand;
        },
        subscriptionsPrice() {
            return this.subscription?.price || 0;
        },
    },
    watch: {
        isExpanded(value) {
            if (value === false) this.showEditForm = false;
        },
    },
    components: {
        DeliveryPrice,
        CancelSubscriptionModal,
        EditDeliveryAddressForm,
        AddressManageQuantity,
        RestartDeliveriesAction,
        BoxAccordion,
        PauseButton,
        PauseDeliveries,
        SelectBoxType,
        PauseDeliveriesConfirmationModal,
        DeleteAddressesModal,
        CannotPauseModal,
    },
};
</script>

<style lang="scss" scoped>
.option__top-border {
    border-top: 1px solid black;
}

.cancel-option {
    padding-right: 21px;
}

.cancel-option__icon {
    max-width: 20px;
    max-height: 20px;
}

.edit-option {
    padding-right: 24px;
}

.edit-option__icon {
    max-width: 25px;
    max-height: 25px;
}

.border-bottom {
     border-bottom: 1px solid black;
}
</style>
